import { ContentType } from "@gogovapps/core-service-client";
import { coreService } from "../services/coreService";
import { FirstArgumentType, SecondArgumentType, ThirdArgumentType } from "../types/ArgumentTypes";
import { buildQuery } from "../lib/network";
import {
  NotifySubscribersInfiniteTableType,
  NotifySubscribersInfiniteTableView,
} from "../features/notifications/routes/notify/subscribers/NotifySubscribersInfiniteTable";

export const postNotifyInvite = (headers: any) => (body: FirstArgumentType<typeof coreService.notifyInviteCreate>) => {
  return buildQuery(coreService.notifyInviteCreate(body, { headers }), (response) => {
    return response.data;
  });
};

export const getNotifySMSNotificationInfo = (headers: any) => () => {
  return buildQuery(coreService.notifySmsNotificationInfoList({ headers }), (response) => {
    return response.data;
  });
};

export const getNotifyVoiceNotificationInfo =
  (headers: any) => (queryParameters: FirstArgumentType<typeof coreService.notifyVoiceNotificationInfoList>) => {
    return buildQuery(coreService.notifyVoiceNotificationInfoList(queryParameters, { headers }), (response) => {
      return response.data;
    });
  };

export const getPublicNotifySettings = (headers: any) => () => {
  return buildQuery(coreService.notifySettingsPublicList({ headers }), (response) => {
    return response.data;
  });
};

export const getNotifySettings = (headers: any) => () => {
  return buildQuery(coreService.notifySettingsList({ headers }), (response) => {
    return response.data;
  });
};

export type NotifySettings = Awaited<ReturnType<typeof coreService.notifySettingsList>>["data"];

export const putNotifySettings =
  (headers: any) => (body: FirstArgumentType<typeof coreService.notifySettingsUpdate>) => {
    return buildQuery(
      coreService.notifySettingsUpdate(
        {
          ...body,
          voiceDictionaryRules: body.voiceDictionaryRules ?? [],
        },
        { headers },
      ),
      (response) => {
        return response;
      },
    );
  };

export type unlinkNotifySubscriberContactMethodToPersonResponse = Awaited<
  ReturnType<typeof coreService.notifySubscriberContactMethodDisconnectUpdate>
>["data"];
export const unlinkNotifySubscriberContactMethodToPerson =
  (headers: any) =>
  ({ id }: { id: number }) => {
    console.log("Unlinking subscriber contact method ID:", id);
    return buildQuery(
      coreService.notifySubscriberContactMethodDisconnectUpdate(id, {
        headers: {
          ...headers,
          "Content-Type": "text/plain",
        },
      }),
      (response) => {
        return response.data;
      },
    );
  };

export type PutNotifySettingsBody = FirstArgumentType<typeof coreService.notifySettingsUpdate>;

export const postVoiceNotificationGeneration =
  (headers: any) => (body: FirstArgumentType<typeof coreService.notifyVoiceNotificationGenerateCreate>) => {
    return buildQuery(coreService.notifyVoiceNotificationGenerateCreate(body, { headers }), (response) => {
      return response.data;
    });
  };

export const getPreGeneratedVoiceAudioResources = (headers: any) => () => {
  return buildQuery(coreService.notifyVoiceAudioResourcePreGeneratedList({ headers }), (response) => {
    return response.data;
  });
};

export const putPreGeneratedVoiceAudioResourcesGeneration = (headers: any) => () => {
  return buildQuery(
    coreService.notifyVoiceAudioResourcePreGeneratedGenerateUpdate({ headers, type: ContentType.Text }),
    (response) => {
      return response.data;
    },
  );
};

export type GetNotifySubscriptionGroupsParamsType = { cursor?: number; includeDeactivated?: boolean; take?: number };
export const getNotifySubscriptionGroups =
  (headers: any) =>
  ({ cursor, take, includeDeactivated }: GetNotifySubscriptionGroupsParamsType) => {
    return buildQuery(
      coreService.notifySubscriptionGroupList(
        {
          cursor,
          includeDeactivated: includeDeactivated ? "true" : "false",
          take,
        },
        { headers },
      ),
      (response) => {
        return response.data;
      },
    );
  };

export const getNotifySubscriptionGroupsPublic = (headers: any) => () => {
  return buildQuery(coreService.notifySubscriptionGroupPublicList({ headers }), (response) => {
    return response.data;
  });
};

export type GetNotifySubscriptionGroupResponse = Awaited<
  ReturnType<typeof coreService.notifySubscriptionGroupDetail>
>["data"];
export type SubscriptionGroup = GetNotifySubscriptionGroupResponse;
export const getNotifySubscriptionGroup =
  (headers: any) =>
  ({ id }: { id: number }) => {
    return buildQuery(coreService.notifySubscriptionGroupDetail(id, { headers }), (response) => {
      return response.data;
    });
  };

export type CreateNotifySubscriptionGroupBody = FirstArgumentType<typeof coreService.notifySubscriptionGroupCreate>;
export type CreateNotifySubscriptionGroupResponse = Awaited<
  ReturnType<typeof coreService.notifySubscriptionGroupCreate>
>["data"];
export const postNotifySubscriptionGroup =
  (headers: any) => (body: FirstArgumentType<typeof coreService.notifySubscriptionGroupCreate>) => {
    return buildQuery(coreService.notifySubscriptionGroupCreate(body, { headers }), (response) => {
      return response.data;
    });
  };

export type UpdateNotifySubscriptionGroupBody = SecondArgumentType<typeof coreService.notifySubscriptionGroupUpdate>;
export type UpdateNotifySubscriptionGroupResponse = Awaited<
  ReturnType<typeof coreService.notifySubscriptionGroupUpdate>
>["data"];
export const putNotifySubscriptionGroup =
  (headers: any) =>
  ({ id, body }: { id: number; body: SecondArgumentType<typeof coreService.notifySubscriptionGroupUpdate> }) => {
    return buildQuery(coreService.notifySubscriptionGroupUpdate(id, body, { headers }), (response) => {
      return response.data;
    });
  };

export type DeactivateNotifySubscriptionGroupResponse = Awaited<
  ReturnType<typeof coreService.notifySubscriptionGroupDeactivatedUpdate>
>["data"];
export const deactivateNotifySubscriptionGroup =
  (headers: any) =>
  ({ id }: { id: number }) => {
    return buildQuery(
      coreService.notifySubscriptionGroupDeactivatedUpdate(id, { headers, type: ContentType.Text }),
      (response) => {
        return response.data;
      },
    );
  };

export type ReactivateNotifySubscriptionGroupResponse = Awaited<
  ReturnType<typeof coreService.notifySubscriptionGroupDeactivatedDelete>
>["data"];
export const reactivateNotifySubscriptionGroup =
  (headers: any) =>
  ({ id }: { id: number }) => {
    return buildQuery(
      coreService.notifySubscriptionGroupDeactivatedDelete(id, {
        headers: {
          ...headers,
          "Content-Type": "text/plain",
        },
      }),
      (response) => {
        return response.data;
      },
    );
  };

export type GetNotifySubscriberContactMethodsParamsType = {
  take?: number;
  query?: string;
  status?: NotifySubscribersInfiniteTableView;
  subscriptionGroupIds?: number[];
  types?: NotifySubscribersInfiniteTableType[];
  sort?: { column: string; direction: "asc" | "desc" }[];
};
export type GetNotifySubscriberContactMethodsResponse = Awaited<
  ReturnType<typeof coreService.notifySubscriberContactMethodSearchList>
>["data"];
export const getNotifySubscriberContactMethods =
  (headers: any) =>
  (
    { take, query, status, subscriptionGroupIds, types, sort }: GetNotifySubscriberContactMethodsParamsType,
    cursor?: string,
  ) => {
    return buildQuery(
      coreService.notifySubscriberContactMethodSearchList(
        {
          cursor,
          take,
          query: query || undefined,
          status: status || undefined,
          subscriptionGroupIds: subscriptionGroupIds?.join(",") || undefined,
          types: types?.join(",") || undefined,
          sort: sort?.map((s) => `${s.column}:${s.direction}`).join(",") || undefined,
        },
        { headers },
      ),
      (response) => {
        return response.data;
      },
    );
  };

export type GetNotifyContactsParamsType = {
  take?: number;
  query?: string;
  status?: NotifySubscribersInfiniteTableView;
  subscriptionGroupIds?: number[];
  types?: NotifySubscribersInfiniteTableType[];
  sort?: { column: string; direction: "asc" | "desc" }[];
};
export type GetNotifyContactsResponse = Awaited<ReturnType<typeof coreService.notifyNotifyContactSearchList>>["data"];
export const getNotifyContacts =
  (headers: any) =>
  ({ take, query, subscriptionGroupIds, types, sort }: GetNotifyContactsParamsType, cursor?: string) => {
    return buildQuery(
      coreService.notifyNotifyContactSearchList(
        {
          cursor,
          take,
          query: query || undefined,
          subscriptionGroupIds: subscriptionGroupIds?.join(",") || undefined,
          types: types?.join(",") || undefined,
          sort: sort?.map((s) => `${s.column}:${s.direction}`).join(",") || undefined,
        },
        { headers },
      ),
      (response) => {
        return response.data;
      },
    );
  };

export type GetNotifyPublicNotificationContentParamsType = {
  take?: number;
  query?: string;
  subscriptionGroupIds?: number[];
};
export type GetNotifyPublicNotificationContentSearchResponse = Awaited<
  ReturnType<typeof coreService.notifyNotificationContentSearchPublicList>
>["data"];

export const getNotifyPublicNotificationContent =
  (headers: any, subscriberContactMethodId?: number | undefined) =>
  ({ take, query, subscriptionGroupIds }: GetNotifyPublicNotificationContentParamsType, cursor?: string) => {
    return buildQuery(
      coreService.notifyNotificationContentSearchPublicList(
        {
          cursor,
          take,
          query: query || undefined,
          subscriptionGroupIds: subscriptionGroupIds?.join(",") || undefined,
          ...(subscriberContactMethodId !== undefined && subscriberContactMethodId !== null
            ? { subscriberContactMethodId }
            : {}),
        },
        { headers },
      ),
      (response) => {
        return response.data;
      },
    );
  };

export type GetNotifyNotificationContentParamsType = {
  take?: number;
  query?: string;
  subscriptionGroupIds?: number[];
  departmentGroupId: number | null;
  status: GetNotifyNotificationStatus | null;
};
export type GetNotifyNotificationContentSearchResponse = Awaited<
  ReturnType<typeof coreService.notifyNotificationContentSearchList>
>["data"];
export type GetNotifyNotificationStatus = GetNotifyNotificationContentSearchResponse["data"][number]["status"];

export const getNotifySearchNotificationContent =
  (headers: any) =>
  (
    { take, query, subscriptionGroupIds, departmentGroupId, status }: GetNotifyNotificationContentParamsType,
    cursor?: string,
  ) => {
    const undefinedIfEmptyString = (str: string | undefined | null) => {
      if (str?.length === 0) return undefined;
      return str ?? undefined;
    };

    return buildQuery(
      coreService.notifyNotificationContentSearchList(
        {
          cursor,
          take,
          query: undefinedIfEmptyString(query),
          subscriptionGroupIds: undefinedIfEmptyString(subscriptionGroupIds?.join(",")),
          departmentGroupId: departmentGroupId ?? undefined,
          status: status ?? undefined,
        },
        { headers },
      ),
      (response) => {
        return response.data;
      },
    );
  };

export type GetNotifyContent = Awaited<ReturnType<typeof coreService.notifyNotificationContentDetail>>["data"];

export const getNotifyContentDetails =
  (headers: any) =>
  ({ id }: { id: number }) => {
    return buildQuery(coreService.notifyNotificationContentDetail(id, { headers }), (response) => {
      return response.data;
    });
  };

export type PostNotifyNotificationContentResponse = Awaited<
  ReturnType<typeof coreService.notifyNotificationContentCreate>
>["data"];
export type PostNotifyNotificationContentBody = FirstArgumentType<typeof coreService.notifyNotificationContentCreate>;
export const postNotifyNotificationContent = (headers: any) => (body: PostNotifyNotificationContentBody) => {
  return buildQuery(coreService.notifyNotificationContentCreate(body, { headers }), (response) => {
    return response.data;
  });
};

export type PutNotifyNotificationContentResponse = Awaited<
  ReturnType<typeof coreService.notifyNotificationContentUpdate>
>["data"];
export type PutNotifyNotificationContentBody = SecondArgumentType<typeof coreService.notifyNotificationContentUpdate>;
export type PutNotifyNotificationContentQuery = ThirdArgumentType<typeof coreService.notifyNotificationContentUpdate>;
export const putNotifyNotificationContent =
  (headers: any) =>
  ({
    id,
    body,
    query,
  }: {
    id: number;
    body: PutNotifyNotificationContentBody;
    query: PutNotifyNotificationContentQuery;
  }) => {
    return buildQuery(coreService.notifyNotificationContentUpdate(id, body, query, { headers }), (response) => {
      return response.data;
    });
  };

export type PostNotifyNotificationContentMassNotificationBody = SecondArgumentType<
  typeof coreService.notifyNotificationContentMassNotificationUpdate
>;
export const putNotifyNotificationContentMassNotification =
  (headers: any) =>
  ({ id, body }: { id: number; body: PostNotifyNotificationContentMassNotificationBody }) => {
    return buildQuery(
      coreService.notifyNotificationContentMassNotificationUpdate(id, body, { headers }),
      (response) => {
        return response.data;
      },
    );
  };

export type linkNotifySubscriberContactMethodToPersonResponse = Awaited<
  ReturnType<typeof coreService.notifySubscriberContactMethodConnectUpdate>
>["data"];

export const linkNotifySubscriberContactMethodToPerson =
  (headers: any) =>
  ({
    id,
    body,
  }: {
    id: number;
    body: SecondArgumentType<typeof coreService.notifySubscriberContactMethodConnectUpdate>;
  }) => {
    return buildQuery(coreService.notifySubscriberContactMethodConnectUpdate(id, body, { headers }), (response) => {
      return response.data;
    });
  };

export type PutNotifySubscriptionPreferencesResponse = Awaited<
  ReturnType<typeof coreService.notifySubscriptionPreferenceUpdate>
>["data"];
export const putNotifySubscriptionPreferences =
  (headers: any) => (body: FirstArgumentType<typeof coreService.notifySubscriptionPreferenceUpdate>) => {
    return buildQuery(coreService.notifySubscriptionPreferenceUpdate(body, { headers }), (response) => {
      return response.data;
    });
  };

export type DeleteNotifySubscriberContactMethodSubscriptionPreferencesResponse = Awaited<
  ReturnType<typeof coreService.notifySubscriberContactMethodSubscriptionPreferenceDelete>
>["data"];
// Deletes All preferences for a given contact method and subscription group; effectivley removing it from the group
export const deleteNotifySubscriberContactMethodSubscriptionPreferences =
  (headers: any) =>
  ({
    id,
    body,
  }: {
    id: number;
    body: SecondArgumentType<typeof coreService.notifySubscriberContactMethodSubscriptionPreferenceDelete>;
  }) => {
    return buildQuery(
      coreService.notifySubscriberContactMethodSubscriptionPreferenceDelete(id, body, { headers }),
      (response) => {
        return response.data;
      },
    );
  };

export type DeleteNotifyContactSubscriptionPreferences = Awaited<
  ReturnType<typeof coreService.notifySubscriptionPreferenceDelete>
>["data"];
export const deleteNotifyContactSubscriptionPreferences =
  (headers: any) => (body: FirstArgumentType<typeof coreService.notifySubscriptionPreferenceDelete>) => {
    return buildQuery(coreService.notifySubscriptionPreferenceDelete(body, { headers }), (response) => {
      return response.data;
    });
  };

export const putNotifySubscriptionPreference =
  (headers: any) =>
  ({
    id,
    body,
  }: {
    id: number;
    body: SecondArgumentType<typeof coreService.notifySubscriberContactMethodSubscriptionPreferenceUpdate>;
  }) => {
    return buildQuery(
      coreService.notifySubscriberContactMethodSubscriptionPreferenceUpdate(id, body, { headers }),
      (response) => {
        return response.data;
      },
    );
  };

export const postNotifySubscriberContactMethodsOptOut =
  (headers: any) => (body: FirstArgumentType<typeof coreService.notifySubscriberContactMethodOptOutCreate>) => {
    return buildQuery(coreService.notifySubscriberContactMethodOptOutCreate(body, { headers }), (response) => {
      return response.data;
    });
  };

/**
 * DANGER: Force opt-in a subscriber contact method and bypass the invite/opt-in process.
 * Justification must be provided for defense against audits.
 */
export const DANGER_postNotifySubscriberContactMethodsOptIn =
  (headers: any) => (body: FirstArgumentType<typeof coreService.notifySubscriberContactMethodOptInCreate>) => {
    return buildQuery(coreService.notifySubscriberContactMethodOptInCreate(body, { headers }), (response) => {
      return response.data;
    });
  };

export type PostNotifyBulkInviteResponse = Awaited<ReturnType<typeof coreService.notifyInviteBulkCreate>>["data"];
export const postNotifyBulkInvite =
  (headers: any) => (body: FirstArgumentType<typeof coreService.notifyInviteBulkCreate>) => {
    return buildQuery(coreService.notifyInviteBulkCreate(body, { headers }), (response) => {
      return response.data;
    });
  };

type WithOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
export type GetNotifySubscriberContactMethodResponse = Awaited<
  ReturnType<typeof coreService.notifySubscriberContactMethodDetail>
>["data"];
export type SubscriberContactMethod = WithOptional<
  GetNotifySubscriberContactMethodResponse,
  "history" | "communicationAttempts"
>;
export const getNotifySubscriberContactMethod =
  (headers: any) =>
  ({ id }: { id: number }) => {
    return buildQuery(coreService.notifySubscriberContactMethodDetail(id, { headers }), (response) => {
      return response.data;
    });
  };

export type GetNotifyContactResponse = Awaited<ReturnType<typeof coreService.notifyNotifyContactDetail>>["data"];
export type GetNotifyContactSubscriberContactMethodsResponse = Awaited<
  ReturnType<typeof coreService.notifyNotifyContactDetail>
>["data"]["contactMethods"][0];
export const getNotifyContact =
  (headers: any) =>
  ({ id }: { id: number }) => {
    return buildQuery(coreService.notifyNotifyContactDetail(id, { headers }), (response) => {
      return response.data;
    });
  };

export type GetNotifySubscriberContactMethodsForLoggedInUserResponse = Awaited<
  ReturnType<typeof coreService.notifySubscriberContactMethodPublicUpdate>
>["data"];

export const getNotifySubscriberContactMethodsForLoggedInUser =
  (headers: any) => (body: FirstArgumentType<typeof coreService.notifySubscriberContactMethodPublicUpdate>) => {
    return buildQuery(coreService.notifySubscriberContactMethodPublicUpdate(body, { headers }), (response) => {
      return response.data;
    });
  };

export type PutNotifySubscriberContactMethodBody = FirstArgumentType<
  typeof coreService.notifySubscriberContactMethodUpdate
>;
export type PutNotifySubscriberContactMethodResponse = Awaited<
  ReturnType<typeof coreService.notifySubscriberContactMethodUpdate>
>["data"];

export const putNotifySubscriberContactMethod =
  (headers: any) => (body: FirstArgumentType<typeof coreService.notifySubscriberContactMethodUpdate>) => {
    return buildQuery(coreService.notifySubscriberContactMethodUpdate(body, { headers }), (response) => {
      return response.data;
    });
  };

export type DeleteNotifySubscriberContactMethodResponse = Awaited<
  ReturnType<typeof coreService.notifySubscriberContactMethodDelete>
>["data"];
export const deleteNotifySubscriberContactMethod =
  (headers: any) =>
  ({ id }: { id: number }) => {
    return buildQuery(
      coreService.notifySubscriberContactMethodDelete(id, {
        headers: {
          ...headers,
          "Content-Type": "text/plain",
        },
      }),
      (response) => {
        return response.data;
      },
    );
  };

export const removeNotifyContactsFromSubscriptionGroups =
  (headers: any) =>
  ({ notifyContactIds, subscriptionGroupIds }: { notifyContactIds: number[]; subscriptionGroupIds: number[] }) => {
    return buildQuery(
      coreService.notifyNotifyContactSubscriptionPreferencesDelete(
        { notifyContactIds, subscriptionGroupIds },
        { headers },
      ),
      (response) => {
        return response.data;
      },
    );
  };

export const addNotifyContactsToSubscriptionGroups =
  (headers: any) =>
  ({
    notifyContactIds,
    subscriptionGroupIds,
    overwriteExistingPreference,
  }: {
    notifyContactIds: number[];
    subscriptionGroupIds: number[];
    overwriteExistingPreference?: boolean;
  }) => {
    return buildQuery(
      coreService.notifyNotifyContactSubscriptionPreferencesUpdate(
        { notifyContactIds, subscriptionGroupIds, overwriteExistingPreference },
        { headers },
      ),
      (response) => {
        return response.data;
      },
    );
  };

export type GetNotifyRolesResponse = Awaited<ReturnType<typeof coreService.notifyRoleDetail>>["data"];
export const getNotifyRolesForPerson =
  (headers: any) =>
  ({ personId }: { personId: number }) => {
    return buildQuery(coreService.notifyRoleDetail(personId, { headers }), (response) => {
      return response.data;
    });
  };

export type PutNotifyRolesForPersonBody = SecondArgumentType<typeof coreService.notifyRoleUpdate>;
export type PutNotifyRolesForPersonResponse = Awaited<ReturnType<typeof coreService.notifyRoleUpdate>>["data"];
export const putNotifyRolesForPerson =
  (headers: any) =>
  ({ personId, body }: { personId: number; body: PutNotifyRolesForPersonBody }) => {
    return buildQuery(coreService.notifyRoleUpdate(personId, body, { headers }), (response) => {
      return response.data;
    });
  };

export type PostNotifyNotificationContentInfoBody = FirstArgumentType<
  typeof coreService.notifyNotificationContentInfoCreate
>;
export type PostNotifyNotificationContentInfoResponse = Awaited<
  ReturnType<typeof coreService.notifyNotificationContentInfoCreate>
>["data"];
export const postNotifyNotificationContentInfo =
  (headers: any) => (body: FirstArgumentType<typeof coreService.notifyNotificationContentInfoCreate>) => {
    return buildQuery(coreService.notifyNotificationContentInfoCreate(body, { headers }), (response) => {
      return response.data;
    });
  };

export const putNotifyNotificationContentArchived = (headers: any) => (id: number) => {
  return buildQuery(
    coreService.notifyNotificationContentArchivedUpdate(id, {
      headers: {
        ...headers,
        "Content-Type": "text/plain",
      },
    }),
    (response) => {
      return response.data;
    },
  );
};

export const putNotifyNotificationContentDeactivated = (headers: any) => (id: number) => {
  return buildQuery(
    coreService.notifyNotificationContentDeactivatedUpdate(id, {
      headers: {
        ...headers,
        "Content-Type": "text/plain",
      },
    }),
    (response) => {
      return response.data;
    },
  );
};
